import { Link, useLocation } from 'react-router-dom';

const labels: Record<string, string> = {
  dashboard: 'Home',
  partners: 'Partners',
  onboarding: 'Onboarding',
  schemas: 'Message Schemas',
  'mapping-designer': 'Mapping Designer',
  'test-lab': 'Test Lab',
  flows: 'Integration Flows',
  builder: 'Builder',
  publish: 'Publish Center',
  executions: 'Monitoring',
  dlq: 'Recovery',
  connectors: 'Delivery Channels',
  settings: 'Settings',
  operations: 'Operations',
  exchanges: 'Exchanges',
  maritime: 'Maritime Operations',
  documents: 'Messages',
  generate: 'Generate Document',
  config: 'Administration',
  mappings: 'Mappings',
  templates: 'Templates',
  'validation-rules': 'Validation Rules',
  'connector-health': 'Delivery Endpoints',
  'inbound-policies': 'Inbound Policies',
  as2: 'AS2 Gateway',
  certificates: 'Certificates',
  'transport-profiles': 'Transport Profiles',
  'etat-chargement': 'Etat de Chargement',
  tryptique: 'Tryptique',
};

const configLabels: Record<string, string> = {
  partners: 'Partners',
  connectors: 'Technical Catalog',
  'connector-health': 'Delivery Endpoints',
  'inbound-policies': 'Inbound Policies',
  mappings: 'Mappings',
  templates: 'Templates',
  'validation-rules': 'Validation Rules',
};

const as2Labels: Record<string, string> = {
  certificates: 'Certificates',
  'transport-profiles': 'Transport Profiles',
  dlq: 'Dead-Letter Queue',
  'outbound-messages': 'Outbound Messages',
  'inbound-messages': 'Inbound Messages (Transport)',
};

const navigablePaths = new Set([
  '/dashboard', '/flows', '/flows/builder', '/flows/publish', '/schemas', '/mapping-designer', '/test-lab',
  '/executions', '/dlq', '/connectors', '/settings', '/operations', '/operations/command-center',
  '/operations/exchanges', '/operations/maritime', '/operations/cusdec-ml', '/operations/scanner',
  '/operations/etat-chargement', '/operations/tryptique',
  '/documents', '/config', '/config/partners', '/config/connectors', '/config/connector-health',
  '/config/inbound-policies', '/mappings', '/templates', '/validation-rules', '/generate', '/partners',
  '/as2', '/as2/outbound-messages', '/as2/inbound-messages', '/as2/certificates',
  '/as2/transport-profiles', '/as2/dlq',
]);

export function Breadcrumbs() {
  const { pathname, search } = useLocation();
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return null;

  let current = '';
  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      <Link to={{ pathname: '/dashboard', search }}>OSS Integration</Link>
      {parts.map((part, index) => {
        current += `/${part}`;
        const isLast = index === parts.length - 1;
        const label = labelForPart(part, parts[index - 1], parts);
        return isLast || !navigablePaths.has(current)
          ? <span key={current} aria-current={isLast ? 'page' : undefined}>{label}</span>
          : <Link key={current} to={{ pathname: current, search }}>{label}</Link>;
      })}
    </nav>
  );
}

function labelForPart(part: string, previous: string | undefined, parts: string[]): string {
  if (previous === 'config' && configLabels[part]) {
    return configLabels[part];
  }

  if (previous === 'as2' && as2Labels[part]) {
    return as2Labels[part];
  }

  if (previous === 'partners' && part === 'onboarding') {
    return 'Partner Onboarding';
  }

  if (previous === 'outbound-messages') {
    return 'Outbound Message Detail';
  }

  if (previous === 'inbound-messages' && parts.includes('as2')) {
    return 'Inbound Message Detail';
  }

  if (looksLikeIdentifier(part)) {
    if (previous === 'documents') return 'Document Detail';
    if (previous === 'exchanges') return 'Exchange Detail';
    if (parts.includes('config')) return 'Record Detail';
    return 'Detail';
  }

  return labels[part] ?? formatPathLabel(part);
}

function looksLikeIdentifier(value: string): boolean {
  return /^(doc|exg|flow|flowexec|partner|conn|map|tpl|rule|schema)[_-]/i.test(value) || /^[0-9a-f-]{24,}$/i.test(value);
}

function formatPathLabel(value: string): string {
  return value.replace(/[-_]/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase());
}
